import { CLASSIC_ANALYSIS_RESULTS_LABELS } from "./classicOutputTexts";

function getDataPerPart(mainCalc) {
  return Array.isArray(mainCalc?.calculatedData?.dataPerPart) ? mainCalc.calculatedData.dataPerPart : [];
}

export function getClassicResultsPrisaData(mainCalc) {
  return getDataPerPart(mainCalc).reduce(
    (acc, part) => {
      const prisaYears = Array.isArray(part.prisaData)
        ? part.prisaData.reduce((maxYears, personPrisaData) => (
          Math.max(maxYears, Array.isArray(personPrisaData) ? personPrisaData.length : 0)
        ), 0)
        : 0;
      acc.maxPrisaYearsAllowed = Math.max(acc.maxPrisaYearsAllowed, prisaYears);
      acc.totalPrisaRev += (part.finalLinearBeforePrisa47 || 0) - (part.finalLinear47 || 0);
      acc.totalPrisaRev += (part.finalMutavBeforePrisa25 || 0) - (part.finalMutav25 || 0);
      acc.totalPrisaRev += (part.finalLinearBeforePrisa25 || 0) - (part.finalLinear25 || 0);
      acc.totalPrisaRev += (part.finalLinearBeforePrisa20 || 0) - (part.finalLinear20 || 0);
      acc.totalBeforePrisa += (part.finalLinearBeforePrisa47 || 0)
        + (part.finalMutavBeforePrisa25 || 0)
        + (part.finalLinearBeforePrisa25 || 0)
        + (part.finalLinearBeforePrisa20 || 0)
        + (part.finalInflationLinear10 || 0);
      acc.totalPreAfkaa += (part.finalLinearTaxAllPreAfkaa21 || 0) + (part.finalMutavTaxAllPreAfkaa21 || 0);
      return acc;
    },
    { totalPrisaRev: 0, maxPrisaYearsAllowed: 0, totalBeforePrisa: 0, totalPreAfkaa: 0 }
  );
}

const OFFSET_TAX_BUCKETS = {
  linear: [
    { suffix: "Linear47", percentField: "firstLinearPercent", fallback: 0.47 },
    { suffix: "Linear20", percentField: "secondLinearPercent", fallback: 0.2 },
    { suffix: "Linear25", percentField: "thirdLinearPercent", fallback: 0.25 },
    { suffix: "Inflation10", percentField: "inflationPercent", fallback: 0.1 }
  ],
  mutav: [
    { suffix: "Mutav25", percentField: "mutavPercent", fallback: 0.25 }
  ]
};

// mainCalcService.calcHistoryTax subtracts only these offset buckets from the nominal gain.
const CEILING_OFFSET_SUFFIXES = {
  linear: ["Linear47", "Linear20", "Linear25"],
  mutav: ["Mutav25"]
};

const SIDES = {
  linear: {
    taxPreAfkaa: "finalLinearTaxAllPreAfkaa21",
    taxAfterAfkaa: "finalLinearTaxAll24",
    afkaaCredit: "finalLinearAfkaaZikui22",
    ceiling: "historyTaxLinearSeif25",
    nominalShevach: "shevahMekarkeinRegTax8",
    prisaPairs: [
      ["finalLinearBeforePrisa47", "finalLinear47"],
      ["finalLinearBeforePrisa25", "finalLinear25"],
      ["finalLinearBeforePrisa20", "finalLinear20"]
    ]
  },
  mutav: {
    taxPreAfkaa: "finalMutavTaxAllPreAfkaa21",
    taxAfterAfkaa: "finalMutavTaxAll24",
    afkaaCredit: "finalMutavAfkaaZikui22",
    ceiling: "historyTaxMutavSeif25",
    nominalShevach: "shevahMekarkeinMutav8",
    prisaPairs: [["finalMutavBeforePrisa25", "finalMutav25"]]
  }
};

function readPartTaxRate(part, fieldName, fallback) {
  const raw = part?.[fieldName];
  const parsed = typeof raw === "string" ? parseFloat(raw.replace("%", "")) : Number(raw);
  if (!Number.isFinite(parsed) || parsed <= 0) return fallback;
  return parsed > 1 ? parsed / 100 : parsed;
}

function getPositiveAmount(value) {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) && numericValue > 0 ? numericValue : 0;
}

function getNumber(value) {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : 0;
}

function getRabinovichSaving(part, sideKey, hasPrisa) {
  const percent = Number(part?.discountRabPercent) || 0;
  if (!(percent > 0) || percent >= 1) return 0;
  const discountedTax = SIDES[sideKey].prisaPairs.reduce(
    (sum, [beforePrisaField, finalField]) => sum + getNumber(part?.[hasPrisa ? beforePrisaField : finalField]),
    0
  );
  if (!(discountedTax > 0)) return 0;
  return (discountedTax * percent) / (1 - percent);
}

function getOrdinaryOffsetSaving(part, sideKey, offsetPrefix) {
  return OFFSET_TAX_BUCKETS[sideKey].reduce((sum, bucket) => {
    const offsetShevach = getNumber(part?.[`${offsetPrefix}${bucket.suffix}`]);
    if (!(offsetShevach > 0)) return sum;
    return sum + offsetShevach * readPartTaxRate(part, bucket.percentField, bucket.fallback);
  }, 0);
}

function getCeilingOffsetShevach(part, sideKey, offsetPrefix) {
  return CEILING_OFFSET_SUFFIXES[sideKey].reduce(
    (sum, suffix) => sum + getNumber(part?.[`${offsetPrefix}${suffix}`]),
    0
  );
}

function getPrisaSaving(part, sideKey) {
  return SIDES[sideKey].prisaPairs.reduce(
    (sum, [beforePrisaField, finalField]) => (
      part?.[beforePrisaField] === undefined
        ? sum
        : sum + getNumber(part[beforePrisaField]) - getNumber(part?.[finalField])
    ),
    0
  );
}

// mainCalcService keeps the section 48א(ד) ceiling for a side only when it is
// strictly lower than that side's ordinary tax after הפקעה. "לא רלוונטי" is not a number.
export function isClassicSideCeilingWon(part, sideKey) {
  const ceiling = part?.[SIDES[sideKey].ceiling];
  const ordinary = part?.[SIDES[sideKey].taxAfterAfkaa];
  if (typeof ceiling !== "number" || !Number.isFinite(ceiling)) return false;
  const ordinaryValue = Number(ordinary);
  return Number.isFinite(ordinaryValue) && ceiling < ordinaryValue;
}

function getOrdinarySideTrack(part, sideKey, { hasAfkaa, hasPrisa }) {
  const side = SIDES[sideKey];
  const rabinovichDiscount = getRabinovichSaving(part, sideKey, hasPrisa);
  const businessLossOffset = getOrdinaryOffsetSaving(part, sideKey, "kizuzIski");
  const capitalLossOffset = getOrdinaryOffsetSaving(part, sideKey, "kizuzHoni");
  const prisaSavings = hasPrisa ? getPrisaSaving(part, sideKey) : 0;
  return {
    ceilingWon: false,
    capitalGainsTax: getNumber(part?.[side.taxPreAfkaa])
      + rabinovichDiscount + businessLossOffset + capitalLossOffset + prisaSavings,
    rabinovichDiscount,
    businessLossOffset,
    capitalLossOffset,
    prisaSavings,
    afkaaCredit: hasAfkaa ? getNumber(part?.[side.afkaaCredit]) : 0
  };
}

// On the ceiling track the tax is rate × (nominal gain − offsets), halved for הפקעה.
// Rabinovich and spreading never touch it. The rate is not saved, so it is
// recovered from the saved ceiling and the gain it was taken from.
function getCeilingSideTrack(part, sideKey, { hasAfkaa }) {
  const side = SIDES[sideKey];
  const payableCeiling = getNumber(part?.[side.ceiling]);
  const ceilingBeforeAfkaa = hasAfkaa ? payableCeiling * 2 : payableCeiling;
  const businessOffsetShevach = getCeilingOffsetShevach(part, sideKey, "kizuzIski");
  const capitalOffsetShevach = getCeilingOffsetShevach(part, sideKey, "kizuzHoni");
  const ceilingBase = getNumber(part?.[side.nominalShevach]) - businessOffsetShevach - capitalOffsetShevach;
  const ceilingRate = ceilingBase > 0 ? ceilingBeforeAfkaa / ceilingBase : 0;
  const businessLossOffset = businessOffsetShevach * ceilingRate;
  const capitalLossOffset = capitalOffsetShevach * ceilingRate;
  return {
    ceilingWon: true,
    capitalGainsTax: ceilingBeforeAfkaa + businessLossOffset + capitalLossOffset,
    rabinovichDiscount: 0,
    businessLossOffset,
    capitalLossOffset,
    prisaSavings: 0,
    afkaaCredit: ceilingBeforeAfkaa - payableCeiling
  };
}

// The tax track a side actually used, shown before every פירוט reduction.
// For every side: capitalGainsTax minus all five reductions is actualFinal*26.
export function getClassicPartSideTracks(part, mainCalc) {
  const options = {
    hasAfkaa: mainCalc?.afkaa === true,
    hasPrisa: mainCalc?.prisa?.shouldTryPrisa === true
  };
  return Object.keys(SIDES).map((sideKey) => (
    isClassicSideCeilingWon(part, sideKey)
      ? getCeilingSideTrack(part, sideKey, options)
      : getOrdinarySideTrack(part, sideKey, options)
  ));
}

function sumSideTracks(mainCalc, key) {
  return getDataPerPart(mainCalc).reduce(
    (sum, part) => sum + getClassicPartSideTracks(part, mainCalc).reduce((sideSum, track) => sideSum + track[key], 0),
    0
  );
}

export function getClassicResultsCapitalGainsTax(mainCalc) {
  return sumSideTracks(mainCalc, "capitalGainsTax");
}

// Same count classic shows as תקופת פריסה מומלצת. One year is singular; every other count is plural.
export function getClassicPrisaYearUnit(yearCount) {
  return Number(yearCount) === 1 ? "שנה" : "שנים";
}

export function getClassicResultsVariantRows(mainCalc) {
  const hasPrisa = mainCalc?.prisa?.shouldTryPrisa === true;
  const prisaYearCount = hasPrisa ? getClassicResultsPrisaData(mainCalc).maxPrisaYearsAllowed : 0;

  return [
    {
      key: "rabinovichDiscount",
      label: CLASSIC_ANALYSIS_RESULTS_LABELS.rabinovichDiscount,
      amount: sumSideTracks(mainCalc, "rabinovichDiscount")
    },
    {
      key: "businessLossOffset",
      label: CLASSIC_ANALYSIS_RESULTS_LABELS.businessLossOffset,
      amount: sumSideTracks(mainCalc, "businessLossOffset")
    },
    {
      key: "capitalLossOffset",
      label: CLASSIC_ANALYSIS_RESULTS_LABELS.capitalLossOffset,
      amount: sumSideTracks(mainCalc, "capitalLossOffset")
    },
    {
      key: "prisaSavings",
      label: CLASSIC_ANALYSIS_RESULTS_LABELS.prisaSavings,
      yearCount: prisaYearCount,
      amount: getPositiveAmount(sumSideTracks(mainCalc, "prisaSavings"))
    },
    {
      key: "afkaaCredit",
      label: CLASSIC_ANALYSIS_RESULTS_LABELS.afkaaCredit,
      amount: getPositiveAmount(sumSideTracks(mainCalc, "afkaaCredit"))
    }
  ];
}
